import { useState } from 'react';
import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { IconArrowRight as ArrowSVG } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';
import { PlaygroundContainer, SelectionFieldContainer } from '../Buttons/Buttons.styled';

import { dataExternalLinkGroups, linkVariants } from './data';
import { Link } from './Link/Link';
import { CTAContainer } from './Links.styled';

type LinksStructureProps = {
  className?: string;
};

enum PlaygroundLinkType {
  PAIRED_WITH_ICON = 'Paired with icon',
  DEFAULT = 'Default',
}

const getPlaygroundCTA = (linkType: PlaygroundLinkType) => {
  if (linkType === PlaygroundLinkType.PAIRED_WITH_ICON)
    return (
      <Link
        href="#/"
        text="See all documents"
        svg={<ArrowSVG style={{ width: '100%', height: '100%' }} />}
        svgSize={16}
      />
    );

  return <Link href="#/" text="Default" />;
};

const LinksStructure = ({ className = '' }: LinksStructureProps) => {
  const [playgroundLink, setPlaygroundLink] = useState<PlaygroundLinkType>(
    PlaygroundLinkType.PAIRED_WITH_ICON,
  );

  return (
    <div className={className}>
      <div className="content">
        <ContentHeaderStyled
          title="Links"
          breadcrumbs={[
            { label: 'General Components', url: '/general-components' },
            { label: 'Links' },
          ]}
        />
        <div className="page-container">
          <div className="page-content">
            <div className="page-content-with-navigation-wrapper">
              <div className="page-content-with-navigation">
                <div className="page-content-item">
                  <h2 className="h1" id="overview">
                    Overview
                  </h2>
                  <h3 className="h3" id="definition">
                    Definition
                  </h3>
                  <p className="mb-40">
                    Links are interactive elements used to navigate users to other pages, sections,
                    or resources. They are a key component of user interfaces, guiding users to
                    additional content or actions. Properly designed links enhance usability and
                    accessibility, ensuring users can easily identify and interact with them.
                  </p>
                </div>

                <PlaygroundContainer>
                  <SelectionFieldContainer
                    data={linkVariants}
                    title="Variant selector"
                    defaultValue={playgroundLink}
                    onSelect={(linkVariant) => setPlaygroundLink(linkVariant as PlaygroundLinkType)}
                  />
                  <CTAContainer>{getPlaygroundCTA(playgroundLink)}</CTAContainer>
                </PlaygroundContainer>

                <div style={{ marginTop: '40px' }} />
                <div className="page-content-item">
                  <h3 className="h3" id="anatomy">
                    Anatomy
                  </h3>
                  <p className="mb-40">
                    The anatomy of a link consists of its visible text, which conveys the
                    destination or action, and a clickable area, sized for easy interaction. Links
                    come in three sizes—large, medium, and small—but their width depends on the
                    length of the text. Links can be grouped vertically or horizontally, with
                    recommended spacing between them to ensure clarity and usability. Visual cues,
                    such as underlines or hover states, highlight interactivity and enhance
                    accessibility.
                  </p>
                </div>
                <div className="number-cards mb-24">
                  <div className="number-card">
                    <div className="number-card-img">
                      <span className="number-circle">1</span>
                      <img src="/images/general-components-links/screen-anatomy-01@3x.jpg" alt="" />
                    </div>
                  </div>
                  <div className="number-card">
                    <div className="number-card-img">
                      <span className="number-circle">2</span>
                      <img src="/images/general-components-links/screen-anatomy-02@3x.jpg" alt="" />
                    </div>
                  </div>
                </div>
                <p className="mb-40 text-paragraph-medium">
                  1. <strong>Link text:</strong> Communicates what is being linked to. 2.{' '}
                  <strong>Icon (optional)</strong> Indicates the destination or type of content
                  being referenced.
                </p>
                <figure>
                  <img src="/images/general-components-links/screen-anatomy-03@3x.jpg" alt="" />
                  <figcaption>
                    Structure and spacing measurements for Link with icon size / px .
                  </figcaption>
                </figure>
                <figure>
                  <img src="/images/general-components-links/screen-anatomy-04@3x.jpg" alt="" />
                  <figcaption>
                    Structure and spacing measurements for Link grouping / px.
                  </figcaption>
                </figure>
                <div className="page-content-item">
                  <h3 className="h3" id="variants">
                    Variants
                  </h3>
                  <p className="mb-40">
                    Links come in three size variants: small, medium, and large, ensuring
                    flexibility and seamless adaptation to various contexts within a user interface.
                    Inline links align with the type size of the surrounding text to maintain a
                    cohesive flow, while standalone links match the default body copy size for
                    consistency. Link variants are designed to suit different purposes, such as
                    default links for navigation, inline links integrated into text, and
                    button-style links for actionable tasks. Visual distinctions—like size, color,
                    underline styles, or hover effects—enhance usability, clearly communicating each
                    variant&apos;s function while maintaining alignment with the brand’s style and
                    ensuring clarity across the design.
                  </p>
                </div>
                <table className="table table-variants mb-40">
                  <thead>
                    <tr>
                      <th style={{ width: '15.625rem' }}>Element</th>
                      <th>Property</th>
                      <th>px I rem</th>
                      <th>Spacing token</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td valign="top">Icon: small link</td>
                      <td>padding-right</td>
                      <td>16 / 1</td>
                      <td>
                        <div className="role-color">$rithm-xs</div>
                      </td>
                    </tr>
                    <tr>
                      {/* The frame leaves this cell blank: the row continues "Icon: small link". */}
                      <td valign="top" aria-label="Icon: small link" />
                      <td>padding-bottom</td>
                      <td>4 / 0.25</td>
                      <td>
                        <div className="role-color">$rithm-xs</div>
                      </td>
                    </tr>
                    <tr>
                      <td valign="top">Icon: medium link</td>
                      <td>svg</td>
                      <td>12 x 12</td>
                      <td>
                        <div className="role-color">$rithm-s</div>
                      </td>
                    </tr>
                    <tr>
                      <td valign="top">Text</td>
                      <td>Text</td>
                      <td>Text</td>
                      <td>
                        <div className="role-color">$rithm-m</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <h2 className="h1 divider" id="specifications">
                  Specifications
                </h2>
                <div className="page-content-item">
                  <div className="visually-hidden" aria-hidden="true" id="standalone">
                    Standalone
                  </div>
                  <h3 className="h3">Standalone link</h3>
                  <p className="mb-40">
                    A standalone link is an independent navigational element used to direct users to
                    another page, section, or resource without being embedded within text. These
                    links are typically styled to match the default body copy size and maintain
                    clear visual distinction through characteristics like color, hover effects, or
                    underlines. Standalone links are ideal for prominent actions or destinations,
                    ensuring they are easily noticeable and accessible, while adhering to the
                    overall design’s consistency and usability principles.
                  </p>
                </div>
                <figure>
                  <img
                    src="/images/general-components-links/screen-standalone-link@2x.webp"
                    width={1000}
                    height={330}
                    alt=""
                  />
                  <figcaption>
                    Indicates the destination or type of content being referenced.
                  </figcaption>
                </figure>
                <div className="page-content-item mb-40">
                  <div className="visually-hidden" aria-hidden="true" id="inline">
                    Inline
                  </div>
                  <h3 className="h3">Inline link</h3>
                  <p className="mb-40">
                    An inline link is seamlessly integrated within a block of text, providing
                    navigation or additional information without disrupting the content flow. Styled
                    to align with the surrounding type size, inline links maintain readability while
                    standing out through subtle visual cues like underlines or color changes. They
                    are ideal for embedding contextual actions or references, ensuring a cohesive
                    and user-friendly design.
                  </p>
                </div>
                <figure>
                  <img
                    src="/images/general-components-links/screen-inline-link@2x.webp"
                    width={1000}
                    height={433}
                    alt=""
                  />
                  <figcaption>Example shows inline links within the body text</figcaption>
                </figure>
                <h2 className="h1 divider" id="guidelines">
                  Guidelines
                </h2>
                <div className="page-content-item mb-40">
                  <h3 className="h3" id="sizing">
                    Sizing
                  </h3>
                  <p className="mb-40">
                    Link sizing ensures clarity and harmony within the user interface by adapting to
                    context and type size. There are three fixed link heights: small (16px), medium
                    (18px), and large (22px). Inline links should match the font size of the
                    surrounding text, blending naturally into paragraphs or headings. Standalone
                    links, on the other hand, align with the default body copy size of the page,
                    ensuring consistency and prominence. These sizing standards provide flexibility
                    while maintaining readability and visual balance.
                  </p>
                </div>
                <table className="table table-variants text-body12 mb-40">
                  <thead>
                    <tr>
                      <th>Element</th>
                      <th>Height</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td valign="top">Small</td>
                      <td>16 / 1</td>
                    </tr>
                    <tr>
                      <td valign="top">Medium</td>
                      <td>18 / 1.125</td>
                    </tr>
                    <tr>
                      <td valign="top">Large</td>
                      <td>22 / 1.375</td>
                    </tr>
                  </tbody>
                </table>
                <figure>
                  <img src="/images/general-components-links/screen-sizing@3x.jpg" alt="" />
                  <figcaption>Sizing applies to both standalone and inline links.</figcaption>
                </figure>
                <div className="page-content-item mb-40">
                  <div className="visually-hidden" aria-hidden="true" id="areas">
                    Areas
                  </div>
                  <h3 className="h3">Clickable areas</h3>
                  <p>
                    Clickable areas are designed to ensure that users can easily interact with UI
                    elements. The clickable area should be large enough to comfortably accommodate
                    user interaction, typically extending beyond the visual boundaries of the
                    element itself. For optimal usability, ensure that clickable areas are at least
                    44px by 44px, adhering to accessibility guidelines and providing a smooth
                    experience across various devices and platforms. This ensures that actions can
                    be performed efficiently, with minimal frustration for users.
                  </p>
                </div>
                <div className="number-cards">
                  <div className="number-card">
                    <div className="number-card-img">
                      <span className="number-circle">1</span>
                      <img
                        src="/images/general-components-links/screen-clickable-areas-01@3x.jpg"
                        alt=""
                      />
                    </div>
                    <div className="number-card-desc">
                      <p className="text-paragraph-medium">
                        Standalone and inline links with their clickable areas.
                      </p>
                    </div>
                  </div>
                  <div className="number-card">
                    <div className="number-card-img">
                      <span className="number-circle">2</span>
                      <img
                        src="/images/general-components-links/screen-clickable-areas-02@3x.jpg"
                        alt=""
                      />
                    </div>
                  </div>
                </div>
                <h2 className="h1 divider" id="references">
                  References
                </h2>
                {dataExternalLinkGroups.map((externalLinkGroup) => (
                  <ExternalLinkGroupStyled
                    key={externalLinkGroup.id}
                    title={externalLinkGroup.title}
                    cards={externalLinkGroup.cards}
                  />
                ))}
              </div>
              <div>
                <div className="page-navigation">
                  <h2>Contents</h2>
                  <Scrollspy
                    ids={[
                      'overview',
                      'definition',
                      'anatomy',
                      'variants',
                      'specifications',
                      'standalone',
                      'inline',
                      'guidelines',
                      'sizing',
                      'areas',
                      'references',
                    ]}
                    offset={0}
                    activeItemClassName="active"
                    itemContainerClassName="multilevel-link-list"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <PaginationStyled
          previousLink="/general-components/buttons"
          previous="Buttons"
          nextLink="/general-components/form-elements"
          next="Form Elements"
        />
      </div>
    </div>
  );
};

const Links = styled(LinksStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 3, 4, 6, 7, 9, 10])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }
`;

export default Links;
